import type { IntlShape } from 'react-intl';
import { formatTranslation, userRoleExplanationLabelKey, userRoleLabelKey, userRolePricingLabelKey } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import { getAccessLevelOnIconView } from 'shared/domain/issue/mapping/accessLevel/toOnView/getAccessLevelOnIconView';
import type { UserAccess } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import type { SiteModel } from 'shared/domain/site/types/model';
import type {
  UserInDto,
  UserWithPermissionsInDto,
} from 'shared/domain/user/types/inDto';
import type {
  TargetUserOnAccessChangeView,
  UserEditOnView,
  UserLabelledWithEmail,
  UserLabelledWithEmailAndTranslatedRole,
  UserOnView,
  UserOnViewWithIssueAccessLevel,
} from 'shared/domain/user/types/view';
import type { ProcessType } from 'shared/domain/process/types/model';
import {
  type CreatableUserRole,
  UserRole,
} from 'shared/types/userRole';
import { idsToLabelledEntities } from 'shared/utils/id/idsToLabelledEntities';
import { anyToSupportedISO6391LanguageCode } from 'shared/utils/locale/anyToSupportedISO6391LanguageCode';
import { getHelpPageAboutUserRolesUrl } from 'shared/utils/locale/getHelpPageUrl';
import type { PersonalData, UserEntity } from '../types/entity';
import type { LabelledUserModel, UserModel, UserWithPersonalData } from '../types/model';
import { getRoleFromUserWithPermissionsInDto } from './userRole';
import { isAdmin } from 'shared/domain/role/isAdmin';
import type { LabelledEntity } from 'shared/types/commonView';

/**
 * Returns labelled entities for sites/processes based on user role.
 *
 * For admin roles: returns ALL available items because admins have implicit
 * access to everything (their stored arrays are empty by design).
 *
 * For non-admin roles: returns only the items the user has explicit access to.
 *
 * Note: In edit forms, admin sites/processes fields are disabled (read-only display).
 * The save logic in `createPermissionsForExistingUser` handles admin roles separately
 * and never sends sites/processes to backend for admins - it sends admin permission
 * structure instead. So returning all items here is safe and purely for display.
 *
 * @see createPermissionsForExistingUser in toModel.ts
 * @see isSitesAndProcessesFieldDisabled in user/form/index.tsx
 */
function getSitesOrProcessesForRole<T extends { _id: string; label: string }>(
  userRole: UserRole,
  userItemIds: string[],
  availableItems: T[] | undefined
): LabelledEntity[] {
  if (!availableItems) {
    return [];
  }
  if (isAdmin(userRole)) {
    return availableItems.map((item) => ({ _id: item._id, label: item.label }));
  }
  return idsToLabelledEntities(userItemIds, availableItems);
}

function userToLabelledEntityWithEmail(
  user: UserWithPersonalData,
  organizationId: string
): UserLabelledWithEmail {
  return {
    _id: user._id,
    label: getUserLabelByOrganization(user, organizationId),
    email: user.email,
  };
}

export function userInDtoToLabelledWithEmail(
  user: UserInDto,
  organizationId: string
): UserLabelledWithEmail {
  return userToLabelledEntityWithEmail(user, organizationId);
}

export function userModelToLabelledWithEmail(
  user: UserModel,
  organizationId: string
): UserLabelledWithEmail {
  return userToLabelledEntityWithEmail(user, organizationId);
}

export function userModelToUserModelLabelled(
  user: UserModel,
  organizationId: string
): LabelledUserModel {
  return {
    ...user,
    label: getUserLabelByOrganization(user, organizationId),
  };
}

export function userModelToLabelledEntityWithEmailAndTranslatedRole(
  user: UserModel,
  organizationId: string,
  intl: IntlShape
): UserLabelledWithEmailAndTranslatedRole {
  const { label, email } = userToLabelledEntityWithEmail(
    user,
    organizationId
  );
  return {
    _id: user._id,
    label,
    email,
    role: translateRole(user.role, intl),
  };
}

export function translateRole(role: UserRole, intl: IntlShape): string {
  return formatTranslation(intl, userRoleLabelKey(role));
}

export function translateRoleTooltipMessage(
  role: UserRole,
  intl: IntlShape
): string {
  return formatTranslation(intl, userRoleExplanationLabelKey(role));
}

export function translateRolePricing(
  role: UserRole,
  intl: IntlShape
): string {
  return formatTranslation(intl, userRolePricingLabelKey(role));
}

export function getRolePricingColor(role: UserRole): string {
  return role === UserRole.standard || role === UserRole.visible_no_access
    ? 'success'
    : 'warning';
}

export function toLabelledUserRole<T extends UserRole | CreatableUserRole>(
  role: T,
  intl: IntlShape
): {
  _id: T;
  label: string;
  tooltipMessage: string;
  chipLabel: string;
  chipColor: string;
  footerLink: string;
} {
  return {
    _id: role,
    label: translateRole(role, intl),
    tooltipMessage: translateRoleTooltipMessage(role, intl),
    footerLink: getHelpPageAboutUserRolesUrl(
      anyToSupportedISO6391LanguageCode(intl.locale)
    ),
    chipLabel: translateRolePricing(role, intl),
    chipColor: getRolePricingColor(role),
  };
}

export function userWithPersonalDataToUserOnViewWithIssueAccessLevel(
  user: UserWithPersonalData,
  organizationId: string,
  intl: IntlShape,
  userAccesses: UserAccess[],
  processes: ProcessType[],
  sites: SiteModel[]
): UserOnViewWithIssueAccessLevel {
  return {
    _id: user._id,
    label: getUserLabelByOrganization(user, organizationId),
    phone: getUserPhoneByOrganization(user, organizationId),
    email: user.email,
    role: toLabelledUserRole(user.role as CreatableUserRole, intl),
    processes: getSitesOrProcessesForRole(user.role, user.processes, processes),
    sites: getSitesOrProcessesForRole(user.role, user.sites, sites),
    accessLevel: getAccessLevelOnIconView(userAccesses, user._id, user.role),
  };
}
export function userModelToUserOnView({
  userModel,
  sites,
  processes,
  intl,
  organizationId,
}: {
  userModel: UserModel;
  sites: SiteModel[];
  processes: ProcessType[];
  intl: IntlShape;
  organizationId: string;
}): UserOnView {
  return {
    _id: userModel._id,
    label: getUserLabelByOrganization(userModel, organizationId),
    phone: getUserPhoneByOrganization(userModel, organizationId),
    email: userModel.email,
    role: toLabelledUserRole(userModel.role as CreatableUserRole, intl),
    processes: getSitesOrProcessesForRole(userModel.role, userModel.processes, processes),
    sites: getSitesOrProcessesForRole(userModel.role, userModel.sites, sites),
  };
}

export function userModelToUserOnViewWithAccessLevel({
  userModel,
  sites,
  processes,
  intl,
  organizationId,
  userAccesses,
}: {
  userModel: UserModel;
  sites: SiteModel[];
  processes: ProcessType[];
  intl: IntlShape;
  organizationId: string;
  userAccesses: UserAccess[];
}): UserOnViewWithIssueAccessLevel {
  const userOnView = userModelToUserOnView({
    userModel,
    sites,
    processes,
    intl,
    organizationId,
  });
  return {
    accessLevel: getAccessLevelOnIconView(
      userAccesses,
      userOnView._id,
      userOnView.role._id
    ),
    ...userOnView,
  };
}

export function userModelToUserEditOnView({
  userModel,
  sites,
  processes,
  intl,
  organizationId,
}: {
  userModel: UserModel;
  sites: SiteModel[] | undefined;
  processes: ProcessType[] | undefined;
  intl: IntlShape;
  organizationId: string;
}): UserEditOnView {
  return {
    _id: userModel._id,
    label: getUserLabelByOrganization(userModel, organizationId),
    phone: getUserPhoneByOrganization(userModel, organizationId),
    email: userModel.email,
    role: toLabelledUserRole(userModel.role as CreatableUserRole, intl),
    processes: getSitesOrProcessesForRole(userModel.role, userModel.processes ?? [], processes),
    sites: getSitesOrProcessesForRole(userModel.role, userModel.sites ?? [], sites),
    personalData: userModel.personalData,
    canBePersonalDataModified: userModel.canBePersonalDataModified,
  };
}

export function getPersonalDataByOrganization(
  user: { personalData?: PersonalData[]; email: string },
  organizationId: string | null
): PersonalData {
  if (!organizationId) {
    return {
      clientId: '',
      label: user?.email ?? '',
      phone: '',
    };
  }
  const personalData = user?.personalData;
  if (!personalData || !Array.isArray(personalData)) {
    return {
      clientId: organizationId,
      label: user?.email ?? '',
      phone: '',
    };
  }
  const personalDataForRequestedClient = personalData.find(
    (data) => data.clientId === organizationId
  );
  if (!personalDataForRequestedClient) {
    return {
      clientId: organizationId,
      label: user?.email ?? '',
      phone: '',
    };
  }
  return {
    clientId: organizationId,
    label: personalDataForRequestedClient.label,
    phone: personalDataForRequestedClient.phone,
  };
}

export function setPersonalDataForOrganization(
  personalData: PersonalData[],
  organizationId: string,
  newData: Partial<PersonalData>
): PersonalData[] {
  const personalDataForRequestedClient = personalData.find(
    (data) => data.clientId === organizationId
  );

  if (!personalDataForRequestedClient) {
    throw new Error(
      `Could not find requested organization in personal data ${organizationId}`
    );
  }

  personalDataForRequestedClient.label =
    newData.label ?? personalDataForRequestedClient.label;
  personalDataForRequestedClient.phone =
    newData.phone ?? personalDataForRequestedClient.phone;

  return personalData;
}

export function getUserLabelOptionalByOrganization(
  user: UserWithPersonalData | undefined,
  organizationId: string | null
): string | undefined {
  if (!user) {
    return;
  }
  return getPersonalDataByOrganization(user, organizationId).label;
}

export function getUserLabelRequiredByOrganization(
  user: UserWithPersonalData | undefined,
  organizationId: string | null,
  intl: IntlShape
): string | undefined {
  if (!user) {
    return;
  }
  return getUserLabelRequired(
    getPersonalDataByOrganization(user, organizationId).label,
    intl
  );
}

export function getUserLabelRequired(
  label: string | undefined,
  intl: IntlShape
): string {
  return label || formatTranslation(intl, CommonTranslationKeys.userDeletedLabel);
}

export function getUserDisplayNameWithEmailFallback(
  user: UserWithPersonalData | undefined,
  organizationId: string | null,
  intl: IntlShape
): string {
  if (!user) {
    return formatTranslation(intl, CommonTranslationKeys.userDeletedLabel);
  }
  const fromOrg = getUserLabelOptionalByOrganization(user, organizationId);
  const trimmed = fromOrg?.trim() ?? '';
  if (trimmed) {
    return trimmed;
  }
  return user.email;
}

export function getUserEmailOrEmpty(user: UserWithPersonalData | undefined): string {
  return user?.email ?? '';
}

export function getUserLabelByOrganization(
  user: { personalData: PersonalData[]; email: string },
  organizationId: string | null
): string {
  return getPersonalDataByOrganization(user, organizationId).label;
}

export function getUserPhoneByOrganization(
  user: { personalData: PersonalData[]; email: string },
  organizationId: string | null
): string {
  return getPersonalDataByOrganization(user, organizationId).phone;
}

function toLabelledUser(
  user: UserModel | UserEntity,
  organizationId: string | null
): LabelledUser {
  return {
    _id: user._id,
    email: user.email,
    label: getUserLabelByOrganization(user, organizationId),
    role: user.role,
  };
}

function toLabelledUserWithSitesAndProcesses(
  user: UserModel | UserEntity,
  organizationId: string | null
): LabelledUserWithSitesAndProcesses {
  return {
    _id: user._id,
    email: user.email,
    label: getUserLabelByOrganization(user, organizationId),
    role: user.role,
    sites: user.sites,
    processes: user.processes,
    phone: getUserPhoneByOrganization(user, organizationId),
  };
}

function toOptionalLabelledUser(
  user: UserModel | UserEntity | undefined,
  organizationId: string | null
): LabelledUser | undefined {
  if (!user) return undefined;
  return {
    _id: user._id,
    email: user.email,
    label: getUserLabelByOrganization(user, organizationId),
    role: user.role,
  };
}

export function toOptionalUserWithPersonalData(
  user: UserModel | UserEntity | undefined
): UserWithPersonalData | undefined {
  if (!user) return undefined;
  return {
    _id: user._id,
    email: user.email,
    personalData: user.personalData,
    role: user.role,
    processes: user.processes,
    sites: user.sites,
  };
}

export function userModelToLabelledWithSitesAndProcesses(
  user: UserModel,
  organizationId: string
): LabelledUserWithSitesAndProcesses {
  return toLabelledUserWithSitesAndProcesses(user, organizationId);
}
export function userModelToOptionalLabelled(
  user: UserModel | undefined,
  organizationId: string
): LabelledUser | undefined {
  return toOptionalLabelledUser(user, organizationId);
}

export function userModelToUserWithPersonalData(
  user: UserModel | undefined
): UserWithPersonalData | undefined {
  return toOptionalUserWithPersonalData(user);
}

export function userEntityToLabelled(
  user: UserEntity,
  organizationId: string
): LabelledUser | undefined {
  return toLabelledUser(user, organizationId);
}

export type LabelledUser = {
  _id: string;
  label: string;
} & { email: string; role: UserRole };

export type LabelledUserWithSitesAndProcesses = {
  _id: string;
  label: string;
} & {
  email: string;
  role: UserRole;
  sites: string[];
  processes: string[];
  phone: string;
};

export function userWithPersonalDataToLabelledUser(
  user: UserWithPersonalData,
  organizationId: string | null
): LabelledUser {
  return {
    _id: user._id,
    email: user.email,
    label: getUserLabelByOrganization(user, organizationId),
    role: user.role,
  };
}

export function userWithPersonalDataToOptionalLabelled(
  user: UserWithPersonalData | undefined,
  organizationId: string | null
): LabelledUser | undefined {
  if (!user) {
    return undefined;
  }
  return {
    _id: user._id,
    email: user.email,
    label: getUserLabelByOrganization(user, organizationId),
    role: user.role,
  };
}

export function userWithPermissionsInDtoToUserWithPersonalData(
  user: UserWithPermissionsInDto,
  organizationId: string,
  currentProjectId: string
): UserWithPersonalData {
  return {
    _id: user._id,
    email: user.email,
    personalData: user.personalData,
    role: getRoleFromUserWithPermissionsInDto(user, organizationId, currentProjectId),
    processes:
      user.permissions.find(
        (permission) => permission.project === currentProjectId
      )?.processes || [],
    sites:
      user.permissions.find(
        (permission) => permission.project === currentProjectId
      )?.sites || [],
  };
}

export function userWithPermissionsInDtoToLabelled(
  userWithPermissionsInDto: UserWithPermissionsInDto,
  organizationId: string,
  currentProjectId: string
): LabelledUser {
  const { _id, email } = userWithPermissionsInDto;

  return {
    _id,
    email,
    label: getUserLabelByOrganization(
      userWithPermissionsInDto,
      organizationId
    ),
    role: getRoleFromUserWithPermissionsInDto(
      userWithPermissionsInDto,
      organizationId,
      currentProjectId
    ),
  };
}

export function userOnViewWithIssueAccessLevelToTargetUserOnAccessChangeView(
  user: UserOnViewWithIssueAccessLevel
): TargetUserOnAccessChangeView {
  return {
    _id: user._id,
    role: user.role._id,
    sites: user.sites.map((site) => site._id),
    processes: user.processes.map((process) => process._id),
    label: user.label,
    email: user.email,
  };
}
